import { applyWorkspaceTemplate } from '@secureblob/sdk'
import {
    COMPONENT_JOB_STARTER_TEMPLATES,
    createComponentJobScaffold,
    createComponentJobScaffoldTemplate,
    serializeComponentJobConfig,
    type ComponentJobScaffold,
    type ComponentJobStarterTemplate,
} from '@secureblob/sdk/componentJobs'

interface ScaffoldWorkspaceWriter {
    readFile: (path: string) => Promise<string | null>
    mkdir: (path: string) => void
    writeFile: (path: string, content?: string) => void
}

interface ApplyStarterScaffoldOptions extends ScaffoldWorkspaceWriter {
    jobRootPath: string
    template: ComponentJobStarterTemplate
}

interface ScaffoldNewJobRootOptions extends ScaffoldWorkspaceWriter {
    jobRootPath: string
}

export function listComponentJobStarterScaffoldsForJobRoot(jobRootPath: string): ComponentJobScaffold[] {
    return COMPONENT_JOB_STARTER_TEMPLATES.map(template => createComponentJobScaffold(jobRootPath, { template }))
}

export function listComponentJobStarterScaffoldsForSourceFile(sourcePath: string): ComponentJobScaffold[] {
    return COMPONENT_JOB_STARTER_TEMPLATES
        .map(template => createSourceCompatibleComponentJobScaffold(sourcePath, template))
        .filter((scaffold): scaffold is ComponentJobScaffold => scaffold !== null)
}

export function createSourceCompatibleComponentJobScaffold(
    sourcePath: string,
    template: ComponentJobStarterTemplate,
): ComponentJobScaffold | null {
    const scaffold = createComponentJobScaffold(deriveVirtualJobRootPath(sourcePath), { template })
    return isSourceCompatibleStarterScaffold(scaffold) ? scaffold : null
}

export async function scaffoldNewComponentJobRoot(options: ScaffoldNewJobRootOptions): Promise<ComponentJobScaffold> {
    const { jobRootPath, readFile, mkdir, writeFile } = options
    const scaffold = createComponentJobScaffold(jobRootPath)

    mkdir(jobRootPath)
    mkdir(`${jobRootPath}/.secureblob`)
    mkdir(`${jobRootPath}/.secureblob/runs`)
    mkdir(`${jobRootPath}/.secureblob/artifacts`)
    mkdir(`${jobRootPath}/.secureblob/builds`)

    writeFile(
        `${jobRootPath}/job.json`,
        `${serializeComponentJobConfig({ entry: getScaffoldEntrypoint(scaffold) })}\n`,
    )

    await materializeScaffoldFiles(jobRootPath, scaffold, { readFile, mkdir, writeFile })
    return scaffold
}

export async function applyStarterScaffoldToJobRoot(options: ApplyStarterScaffoldOptions): Promise<ComponentJobScaffold> {
    const { jobRootPath, template, readFile, mkdir, writeFile } = options
    const scaffold = createComponentJobScaffold(jobRootPath, { template })

    await materializeScaffoldFiles(jobRootPath, scaffold, { readFile, mkdir, writeFile })
    return scaffold
}

type StarterTemplateSession = Parameters<typeof applyWorkspaceTemplate>[0]

async function materializeScaffoldFiles(
    jobRootPath: string,
    scaffold: ComponentJobScaffold,
    writer: ScaffoldWorkspaceWriter,
) {
    const template = createComponentJobScaffoldTemplate(jobRootPath, { template: scaffold.template })
    const result = await applyWorkspaceTemplate(createScaffoldTemplateSession(writer), template)

    if (result.errors.length > 0) {
        throw new Error(result.errors.join('; '))
    }
}

function createScaffoldTemplateSession(writer: ScaffoldWorkspaceWriter): StarterTemplateSession {
    return {
        readText: (path: string) => writer.readFile(path),
        writeText: (path: string, content: string) => writer.writeFile(path, content),
        writeBytes: (path: string) => {
            throw new Error(`Starter scaffolds only support UTF-8 text files (got binary path ${path})`)
        },
        openSqlite: async (path: string) => {
            throw new Error(`Starter scaffolds do not support database template entries (got ${path})`)
        },
        closeSqliteDb: () => {},
        mkdir: writer.mkdir,
    } as unknown as StarterTemplateSession
}

function getScaffoldEntrypoint(scaffold: ComponentJobScaffold): string {
    const primaryFile = scaffold.files[0]
    if (!primaryFile) {
        throw new Error(`Starter scaffold "${scaffold.template}" must define at least one file`)
    }

    return `./${primaryFile.path}`
}

function isSourceCompatibleStarterScaffold(scaffold: ComponentJobScaffold): boolean {
    return scaffold.directories.length === 0
        && scaffold.files.some(f => f.path === 'main.tsx')
        && scaffold.files.every(f => f.path === 'main.tsx' || f.path === 'main.test.ts')
}

function deriveVirtualJobRootPath(sourcePath: string): string {
    const normalizedSourcePath = sourcePath.trim()
    const lastSlash = normalizedSourcePath.lastIndexOf('/')
    const parentPath = lastSlash > 0 ? normalizedSourcePath.slice(0, lastSlash) : ''
    const fileName = lastSlash >= 0 ? normalizedSourcePath.slice(lastSlash + 1) : normalizedSourcePath
    const stem = fileName.replace(/\.[^.]+$/, '') || 'component-job'

    return `${parentPath}/${stem}.job`
}
